import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Panel, BrandButton } from "@/components/PageHeader";
import { Field } from "@/components/event-setup/BasicsSection";
import { LinkListEditor } from "@/components/site-settings/LinkListEditor";
import { EmailListInput } from "@/components/site-settings/EmailListInput";
import { reloadFooter } from "@/components/SiteFooter";
import { api, errMsg } from "@/lib/api";

const General = ({ f, set }) => {
  const txt = (k) => (e) => set({ [k]: e.target.value });
  return (
    <Panel title="About & contact details" testId="site-general-panel">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Short description" className="sm:col-span-2"><Textarea rows={3} value={f.about} onChange={txt("about")} data-testid="site-about-input" /></Field>
        <Field label="Email"><Input value={f.email} onChange={txt("email")} data-testid="site-email-input" /></Field>
        <Field label="Phone"><Input value={f.phone} onChange={txt("phone")} data-testid="site-phone-input" /></Field>
        <Field label="Address" className="sm:col-span-2"><Input value={f.address} onChange={txt("address")} data-testid="site-address-input" /></Field>
        <Field label="Other important information (bottom bar)" className="sm:col-span-2"><Textarea rows={2} value={f.notice} onChange={txt("notice")} placeholder="e.g. office hours, registration deadlines, disclaimers" data-testid="site-notice-input" /></Field>
        <Field label="Copyright line (year added automatically)" className="sm:col-span-2"><Input value={f.copyright} onChange={txt("copyright")} data-testid="site-copyright-input" /></Field>
        <label className="sm:col-span-2 flex items-center gap-2 text-sm"><Switch checked={f.contact_form_enabled} onCheckedChange={(v) => set({ contact_form_enabled: v })} data-testid="site-contact-form-toggle" />Show the “Contact us” form in the footer</label>
        <Field label="Email a copy of each Contact us message to" className="sm:col-span-2"><EmailListInput value={f.notify_emails || []} onChange={(notify_emails) => set({ notify_emails })} /></Field>
      </div>
    </Panel>
  );
};

export default function SiteSettings() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (patch) => setF((s) => ({ ...s, ...patch }));
  useEffect(() => {
    api.get("/site/footer/admin").then((r) => setF(r.data));
  }, []);
  const save = async () => {
    if ([...f.partners, ...f.links].some((l) => !l.label.trim())) return toast.error("Every link needs a name");
    setBusy(true);
    try {
      await api.put("/site/footer", f);
      await reloadFooter();
      toast.success("Footer updated on all pages");
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  if (!f) return <div className="text-gray-500">Loading…</div>;
  return (
    <div data-testid="site-settings-page">
      <PageHeader eyebrow="Super Admin" title="Site Footer & Contact" description="Manage the footer shown on every page. Contact form messages are in Membership → Contact Messages."
        actions={<BrandButton onClick={save} disabled={busy} data-testid="site-save-button"><Save className="w-4 h-4" />{busy ? "Saving…" : "Save footer"}</BrandButton>} />
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
        <div className="space-y-4">
          <General f={f} set={set} />
        </div>
        <div className="space-y-4">
          <LinkListEditor title="Partner websites" items={f.partners} onChange={(partners) => set({ partners })} testId="site-partners" />
          <LinkListEditor title="Social media" items={f.socials} onChange={(socials) => set({ socials })} kind="social" testId="site-socials" />
          <LinkListEditor title="Important links" items={f.links} onChange={(links) => set({ links })} testId="site-links" />
        </div>
      </div>
    </div>
  );
}
